<template>
    <article class="sector-post">
        <div class="sector-post__content">
            <div class="sector-post__creator">
                <UserCard
                    v-if="resource.creator"
                    :user="resource.creator"
                    :borderless="true"
                    :small="true"
                />
            </div>
            <div class="sector-post__details">
                <span class="sector-post__date">
                    <span v-if="resource.is_edited" class="edited">EDITED - </span>
                    {{ resource.date }}
                </span>
                <SectorPopup v-if="resource.sector_options" :sectors="resource.sector_options"/>
            </div>
            <button
                v-if="(resource.can && resource.can.edit) && showActions"
                id="context-menu-button"
                @click.prevent.stop="handleContextMenuClick"
                type="button"
            >
                <SvgIcon icon="filled-context-menu" class="interaction-btn__icon" :is-filled="true"/>
                <span>Open Context Menu</span>
            </button>
            <picture class="sector-post__image" v-if="resource.media[0]">
                <img :src="resource.media[0].url" alt=""/>
            </picture>
            <template v-if="resource.post_content">
                <ReadMore
                    v-if="showReadMore"
                    :content="resource.post_content"
                    :wrapper-class="{'sector-post__copy': true}"
                />
                <div v-html="resource.post_content" v-else class="sector-post__copy"/>
            </template>
            <template v-if="resource.opportunities && resource.opportunities.length > 0">
                <SectorPostCardOpportunity
                    v-for="opportunity in resource.opportunities"
                    :key="resource.id"
                    :resource="opportunity"
                />
            </template>
            <template v-else-if="resource.madds && resource.madds.length > 0">
                <SectorPostCardMadd
                    v-for="madd in resource.madds"
                    :key="resource.id"
                    :resource="madd"
                />
            </template>
            <template v-if="resource.files && resource.files.length > 0">
                <SectorPostCardFiles :files="resource.files"/>
            </template>
            <template v-if="resource.links && resource.links.length > 0">
                <SectorPostCardLink
                    v-for="link in resource.links"
                    :key="link.id"
                    :resource="link"
                />
            </template>
        </div>
        <div class="sector-post__interactions">
            <div class="sector-post__actions">
                <InteractionButton
                    title="Like"
                    icon="filled-like"
                    :icon-filled="true"
                    @click="handleLikeClick"
                    :isComplete="resource.is_liked"
                    :disabled="$wait.is('liking-post')"
                />
                <InteractionButton
                    title="Comments"
                    icon="filled-comment"
                    :icon-filled="true"
                    @click="$emit('comment-click')"
                    :count="resource.comments_count > 0 ? resource.comments_count : null"
                />
            </div>
            <div class="sector-post__count">
                <LikesOverview
                    :sector-post="resource.id"
                    :likes="resource.likes"
                />
            </div>
        </div>
        <div class="sector-post__comments" v-if="loadComments">
            <SectorPostCardComments
                ref="commentsSection"
                :id="resource.id"
            />
        </div>
        <SectorPostCardCommentForm
            v-if="resource.id && loadComments"
            :post-id="resource.id"
            @comment-added="$refs.commentsSection.fetchResource()"
        />
        <vue-simple-context-menu
            v-if="(resource.can && resource.can.edit) && showActions"
            :element-id="`sector-post-${resource.id}-menu`"
            :options="[
                { name: 'Edit', value: 'action_edit', class: 'sector-post-edit' },
                { name: 'Delete', value: 'action_delete', class: 'sector-post-delete' },
            ]"
            ref="sectorPostContextMenu"
            @option-clicked="optionClicked"
        />
    </article>
</template>
<script>
import VueSimpleContextMenu from 'vue-simple-context-menu';
import { mapState } from 'vuex';
import API from '@api';
import UserCard from '@components/User/UserCard';
import SectorPopup from '@components/SectorPopup/SectorPopup';
import ReadMore from '@components/ReadMore';
import InteractionButton from '@components/InteractionButton';
import LikesOverview from '@components/LikesOverview';
import SvgIcon from '@components/SvgIcon';
import Spinner from '@components/Spinner';
import SectorPostCardFiles from '@components/Sector/Post/Card/Files';
import SectorPostCardOpportunity from '@components/Sector/Post/Card/Opportunity';
import SectorPostCardMadd from '@components/Sector/Post/Card/Madd';
import SectorPostCardLink from '@components/Sector/Post/Card/Link';
import SectorPostCardCommentForm from '@components/Sector/Post/Card/CommentForm';
import SectorPostCardComments from '@components/Sector/Post/Card/Comments';

export default {
    name: 'SectorPostCard',
    components: {
        SectorPostCardComments,
        SectorPostCardCommentForm,
        SectorPostCardLink,
        SectorPostCardMadd,
        SectorPostCardOpportunity,
        SectorPostCardFiles,
        Spinner,
        SvgIcon,
        LikesOverview,
        InteractionButton,
        UserCard,
        SectorPopup,
        ReadMore,
        VueSimpleContextMenu,
    },
    props: {
        resource: {
            type: Object,
            required: true,
        },
        loadComments: {
            type: Boolean,
            default: true,
        },
        showReadMore: {
            type: Boolean,
            default: true,
        },
        showActions: {
            type: Boolean,
            default: true,
        },
        sectorId: {
            type: Number,
            required: true,
        },
    },
    computed: {
        ...mapState({
            user: state => state.auth.user,
        }),
    },
    methods: {
        handleLikeClick: async function() {
            this.$wait.start('liking-post');

            try {
                let data;

                if(this.resource.is_liked) {
                    data = await API.unlikeSectorPost(this.resource.id);
                } else {
                    data = await API.likeSectorPost(this.resource.id);
                }

                this.$emit('model-updated', data);
            } catch(err) {
                console.warn(err);
            } finally {
                this.$wait.end('liking-post');
            }
        },
        handleContextMenuClick(event, item) {
            if(this.$refs.sectorPostContextMenu) {
                this.$refs.sectorPostContextMenu.showMenu(event, item);
            }
        },
        optionClicked: function($event) {
            const event = $event.option;

            switch(event.value) {
                case 'action_edit':
                    this.$router.push({
                        name: `sector-post-edit`,
                        params: {
                            post_id: this.resource.id,
                            sector_id: this.sectorId,
                        },
                    });
                    break;
                case 'action_delete':
                    this.handleDeleteClicked();
                    break;
            }
        },
        handleDeleteClicked: function() {
            this.$modal.show('dialog', {
                title: 'Are you sure?',
                text: 'Clicking DELETE will remove this post.',
                buttons: [
                    {
                        class: 'btn btn--danger',
                        title: this.$wait.is('deleting') ? 'Deleting...' : 'DELETE',
                        handler: this.handleDelete.bind(this),
                    },
                ],
            });
        },
        handleDelete: async function() {
            const removePostId = this.resource.id;

            try {
                this.$wait.start('deleting');
                await API.deleteSectorPost(removePostId);
            } catch(err) {
                console.warn(err);
            } finally {
                this.$modal.hide('dialog');
                this.$wait.end('deleting');
                this.$emit('deleted', removePostId);
            }
        },
    },
}
</script>
<style scoped lang="scss">
@import '~@/theme/_variables.scss';

.sector-post {
    border: .1rem solid $border;
    border-radius: .4rem;
    background-color: white;

    .sector-post__content {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        justify-content: space-between;
        padding: 2rem;
        gap: 1.5rem;

        .sector-post__creator {
            flex: 1 0 auto;
        }

        .sector-post__details {
            flex: 1 0 18rem;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            row-gap: .6rem;
            text-align: right;

            .sector-post__date {
                color: $text
            }
        }

        .sector-post__image {
            border-radius: .4rem;
            overflow: hidden;
            aspect-ratio: 665 / 220;
            flex: 0 0 100%;

            img {
                object-fit: cover;
                height: 100%;
                width: 100%;
            }
        }

        .sector-post__copy {
            color: $text;
            flex: 1 0 100%;
        }

        @media screen and (max-width: 575px) {
            padding: 1.5rem;

            .sector-post__creator {
                flex: 1 0 100%;
                width: 100%;
            }

            .sector-post__date .edited {
                display: none;
            }

            .sector-post__details {
                flex: 1 0 100%;
                width: 100%;
                display: flex;
                flex-direction: row-reverse;
                justify-content: space-between;
                align-items: center;
            }
        }
    }

    .sector-post__interactions {
        display: flex;
        align-items: center;
        min-width: 100%;
        border-top: .1rem solid $border;
        padding: 1rem 1.5rem;

        .sector-post__count {
            flex: 0 0 30rem;
        }

        .sector-post__actions {
            display: flex;
            align-items: center;
            column-gap: 1rem;
            flex: 1 1 auto;
        }
    }

    .sector-post__comments {
        padding: 1rem 0;
        border-top: .1rem solid $border;
    }
}

#context-menu-button {
    appearance: none;
    -webkit-appearance: none;
    background-color: transparent;
    border: none;
    padding: 0;
    align-self: flex-start;
    color: $secondary;

    span {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        border: 0;
    }

    svg {
        touch-action: none;
        pointer-events: none;
        margin-right: 0;
        width: 1rem;
        height: 2.2rem;
        margin-left: 1rem;
    }
}
</style>
<style lang="scss">
@import '~@/theme/_variables.scss';

.vue-simple-context-menu {
    top: 0;
    left: 0;
    margin: 0;
    padding: 0;
    display: none;
    list-style: none;
    position: absolute;
    z-index: 1000000;
    background-color: #FFFFFF;
    border-bottom-width: 0;
    font-family: Axiforma, sans-serif;
    box-shadow: 0 6px 20px 0 rgba(49, 49, 74, 0.16);
    border-radius: 4px;

    &.vue-simple-context-menu--active {
        display: block;
    }

    li:first-of-type {
        margin-top: 4px;
    }

    li:last-of-type {
        margin-bottom: 4px;
    }

    .vue-simple-context-menu__item {
        display: flex;
        color: #333333;
        cursor: pointer;
        padding: 5px 15px;
        align-items: center;

        &:hover {
            background-color: #EFEFEF;

        }

        &.sector-post-delete:hover {
            background-color: $accent-header;
            color: #FFFFFF;
        }
    }

    .vue-simple-context-menu__divider {
        box-sizing: content-box;
        height: 2px;
        background-color: #C0CDD1;
        padding: 4px 0;
        background-clip: content-box;
        pointer-events: none;
    }
}
</style>
